import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Truck, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import ProductCard from "../components/ProductCard";
import { FEATURES, HERO_IMAGE, BRAND } from "../mock";
import { useProducts } from "../lib/useProducts";
import Logo from "../components/Logo";
import SEO from "../components/SEO";

const Home = () => {
  const { items: PRODUCTS } = useProducts();
  const featuredSkus = ["RT-10-10MG", "BPC-157", "TB-500", "TIRZ-5", "CJC-IPA-5", "MOTSC-5", "BNDL-ADV", "BNDL-REC"];
  const featured = PRODUCTS.filter((p) => featuredSkus.includes(p.sku)).slice(0, 8);

  // JSON-LD organization markup — helps Google render brand info in search.
  const orgStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND.name,
    url: typeof window !== "undefined" ? window.location.origin : "https://certifiedwellness.net",
    logo: HERO_IMAGE,
    email: BRAND.email,
    telephone: BRAND.phone,
    description:
      "Certified Wellness is a U.S.-based chemical supplier of laboratory research peptides and blends. HPLC-tested. COA on every batch. For research use only.",
  };

  return (
    <div>
      <SEO
        title="Research-Grade Compounds & Peptides"
        description="Certified Wellness — chemical supplier of HPLC-tested research peptides and blends. COA on every batch. Made in the USA. For laboratory research use only."
        canonical="/"
        structuredData={orgStructuredData}
      />
      {/* HERO - full-width clinical banner */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100">
        <div className="max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-8 pt-4">
          <div className="hero-banner-wrap fade-up">
            <img
              src={HERO_IMAGE}
              alt={`${BRAND.name} — Advanced formulas. Targeted results.`}
              className="hero-banner-img"
            />
          </div>
        </div>

        {/* CTA strip below banner */}
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-2 pb-12 lg:pb-16 flex flex-col items-center text-center">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">
              Research Grade · Purpose Driven · Quality You Trust
            </span>
            <div className="h-px w-10 bg-[#b91c1c]" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild size="lg" className="btn-primary h-12 px-8 text-[12px] font-semibold uppercase tracking-[0.18em] rounded-none">
              <Link to="/shop">View Catalog <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-8 text-[12px] font-semibold uppercase tracking-[0.18em] rounded-none border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white">
              <Link to="/shop/blends">View Blends</Link>
            </Button>
          </div>
          <div className="flex items-center gap-3 mt-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#b91c1c]" />
              <span className="font-semibold text-[#0a0a0a] text-[12px] tracking-[0.18em] uppercase">3rd-Party HPLC Tested</span>
            </div>
            <span className="text-gray-400">·</span>
            <span>Made in USA · Discreet Shipping</span>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — compliance-focused (no health claims, no marketing numbers) */}
      <section className="bg-[#0a0a0a] text-white border-y border-[#b91c1c]/30" data-testid="home-trust-strip">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {[
              {
                kicker: "Step 01",
                title: "Precision Lyophilization",
                sub: "Manufactured in a controlled U.S. facility under strict compounding standards.",
              },
              {
                kicker: "Step 02",
                title: "Verified Purity",
                sub: "Every batch third-party tested by HPLC and mass spectrometry.",
              },
              {
                kicker: "Step 03",
                title: "Batch-Level COA",
                sub: "A Certificate of Analysis is on file for every lot we produce.",
              },
              {
                kicker: "Step 04",
                title: "Same-Day Fulfillment",
                sub: "Tracked, discreet, temperature-controlled shipping from the USA.",
              },
            ].map((m) => (
              <div
                key={m.title}
                className="bg-[#0a0a0a] px-6 py-10 lg:py-14 flex flex-col items-start"
              >
                <div className="text-[10px] tracking-[0.32em] font-semibold text-red-400 mb-4">
                  {m.kicker}
                </div>
                <div className="font-display text-2xl lg:text-3xl font-semibold tracking-tight leading-tight">
                  {m.title}
                </div>
                <div className="mt-4 text-[13px] text-gray-400 font-light leading-relaxed max-w-[28ch]">
                  {m.sub}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-6">
            <p className="text-[11px] tracking-[0.28em] uppercase text-gray-400 font-semibold">
              <span className="text-red-400">●</span> For laboratory research use only · Not for human or animal consumption
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-white hover:text-red-400 transition-colors"
            >
              Our process <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES section removed — catalog is presented as a single research catalog,
          not segmented into use-case buckets (compliance preference). */}

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#b91c1c]" />
              <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">From Our Catalog</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight leading-[1.05]">
              Research <span className="font-serif italic font-normal">compounds.</span>
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0a0a0a] hover:text-[#b91c1c]">
            View Catalog <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-[#0a0a0a] text-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-red-400" />
              <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-red-400">Limited Time · 10% off</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tight">
              First order? Save with{" "}
              <span className="font-serif italic font-normal text-red-400">{BRAND.coupon}</span>.
            </h2>
            <p className="mt-5 text-gray-400 max-w-xl font-light text-[16px] leading-relaxed">
              Use code <strong className="text-white font-medium">{BRAND.coupon}</strong> at checkout for 10% off your first order. Free U.S. shipping over $200.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="btn-red rounded-none h-12 px-8 text-[12px] font-semibold uppercase tracking-[0.18em]">
                <Link to="/shop">Shop Now</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="h-12 px-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-white hover:text-red-400 hover:bg-transparent">
                <Link to="/about">Our Process →</Link>
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-center opacity-90">
            <Logo size={220} inverted />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
